import "./_sign-flow-sending-txn-view.scss";

import {Swap, SwapType} from "@tinymanorg/tinyman-js-sdk";

import Spinner from "../../../component/spinner/Spinner";
import Typography from "../../../component/typography/Typography";
import {
  calculateEstimatedSwapImpact,
  getSwapTypeFromFormState
} from "../../../swap/form/SwapForm.utils";
import {useSwapFormState} from "../../../swap/form/context/SwapFormContext";
import {SignFlowState, SignFlowStep} from "../SignFlow.types";
import {algod} from "../../integrations/algosdk/AlgodManager";
import useEffectOnce from "../../util/hook/useEffectOnce";
import {getAssetUnitDisplayName} from "../../../asset/util/assetUtils";
import {getParentUrlFromSearchParams, sendMessageToParentApp} from "../../widgetUtils";
import {
  fetchAndSetAccountData,
  useSelectedAccountAddress,
  useWalletContextDispatch
} from "../../../wallet/util/context/WalletContext.utils";

interface SignFlowSendingTxnViewProps {
  setSignFlowState: React.Dispatch<React.SetStateAction<SignFlowState>>;
  signedTxns: Uint8Array[];
}

function SignFlowSendingTxnView({
  setSignFlowState,
  signedTxns
}: SignFlowSendingTxnViewProps) {
  const state = useSwapFormState();
  const swapType = getSwapTypeFromFormState(state);
  const isFixedInput = swapType === SwapType.FixedInput;
  const swapImpact =
    state.quote &&
    calculateEstimatedSwapImpact({
      state,
      quote: state.quote
    });
  const {quote, txnsToSign} = state;
  const parentUrlOrigin = getParentUrlFromSearchParams();
  const walletContextDispatch = useWalletContextDispatch();
  const selectedAccountAddress = useSelectedAccountAddress();

  useEffectOnce(() => {
    if (quote && txnsToSign) {
      Swap.v2
        .execute({
          quote,
          client: algod.client,
          signedTxns,
          txGroup: txnsToSign
        })
        .then((data) => {
          sendMessageToParentApp(
            {
              message: {type: "SWAP_SUCCESS", response: data}
            },
            parentUrlOrigin
          );

          setSignFlowState({step: SignFlowStep.Success, data});
        })
        .then(() => {
          fetchAndSetAccountData(walletContextDispatch, selectedAccountAddress);
        })
        .catch((error) => {
          setSignFlowState({step: SignFlowStep.Error, error});
        });
    } else if (!quote) {
      setSignFlowState({step: SignFlowStep.Error, error: "No quote available."});
    } else if (!txnsToSign) {
      setSignFlowState({step: SignFlowStep.Error, error: "No transactions to sign."});
    }
  }, [setSignFlowState, signedTxns, quote, txnsToSign]);

  return (
    <div className={"is-centered sign-flow-sending-txn-view"}>
      <Spinner customClassName={"is-centered sign-flow-sending-txn-view__spinner"} />

      <Typography
        as={"h2"}
        tabIndex={0}
        variant={"title"}
        customClassName={"sign-flow-sending-txn-view__title"}>
        {"Sending the transaction"}
      </Typography>

      <Typography
        tabIndex={0}
        customClassName={"sign-flow-sending-txn-view__description"}
        textColor={"text-gray"}>
        {isFixedInput ? "You will receive at least " : "You will spend at most "}

        <Typography
          as={"span"}
          variant={"body"}
          fontWeight={"medium"}
          textColor={"text-gray"}>
          {isFixedInput ? swapImpact?.minimumReceived : swapImpact?.maximumSent}
        </Typography>

        <Typography
          as={"span"}
          variant={"body"}
          fontWeight={"medium"}
          textColor={"text-gray"}>
          {isFixedInput
            ? ` ${getAssetUnitDisplayName(state.assetOut.asset)}`
            : ` ${getAssetUnitDisplayName(state.assetIn.asset)}`}
        </Typography>

        {" or the transaction will revert."}
      </Typography>
    </div>
  );
}

export default SignFlowSendingTxnView;
